import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { createPopper, Instance, Placement } from '@popperjs/core';
// @ts-ignore
import FuzzySearch from 'fuzzy-search';
import { DropDownItem } from '../../../../../models/shared/stylesheet/drop-down-item';
import { sameWidth } from '../../../../../utils/popper-modifiers/same-width.modifier';
import { TrackByUtils } from '../../../../../utils/track-by-utils';

@Component({
  selector: 'app-typeahead-search-input',
  templateUrl: './typeahead-search-input.component.html',
  styleUrls: ['./typeahead-search-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadSearchInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypeaheadSearchInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() set dropdowns(items: DropDownItem[] | null) {
    this._dropdowns.next(items ?? []);
    this.trySelectValue();
  }
  @Input() placeholder: string = $localize`Search`;
  @Input() disabled: boolean = false;
  @Input() error: boolean = false;
  @Input() controlId: string | null = null;
  @Input() dropdownPlacement: Placement = 'bottom-start';

  @Output() itemSelected = new EventEmitter<DropDownItem>();
  @Output() cleared = new EventEmitter<void>();
  @Output() searchTextChanged = new EventEmitter<string>();

  @ViewChild('anchor', { static: false }) anchor!: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownContent', { static: false }) dropdownContent!: ElementRef<HTMLDivElement>;

  private _dropdowns = new BehaviorSubject<DropDownItem[]>([]);

  private _searchText = new BehaviorSubject<string>('');
  public readonly searchText$ = this._searchText.asObservable();

  private _showDropdown = new BehaviorSubject<boolean>(false);
  public readonly showDropdown$ = this._showDropdown.asObservable();

  private _selectedItem = new BehaviorSubject<DropDownItem | null>(null);
  public readonly selectedItem$ = this._selectedItem.asObservable();

  public highlightedIndex: number = -1;

  private popperInstance: Instance | undefined;
  private pendingValue: unknown = undefined;

  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  public readonly filteredItems$: Observable<DropDownItem[]> = combineLatest([this._dropdowns, this._searchText]).pipe(
    map(([items, text]) => this.fuzzyFilter(items, text))
  );

  constructor(private eref: ElementRef, private cdr: ChangeDetectorRef) {}

  public ngOnDestroy(): void {
    this.popperInstance?.destroy();
    this.dropdownContent?.nativeElement.remove();
  }

  public writeValue(value: unknown): void {
    this.pendingValue = value;
    this.trySelectValue();
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.closePanel();
    this.cdr.markForCheck();
  }

  private trySelectValue(): void {
    if (this.pendingValue === undefined) return;
    this._dropdowns.pipe(take(1)).subscribe(items => {
      if (!items.length) return;
      const match = items.find(item => item.getSelectionValue() === this.pendingValue) ?? null;
      this._selectedItem.next(match);
      this._searchText.next(match ? match.getSelectionTitle() : '');
      // Mark consumed so later `dropdowns` re-emits don't wrongly clear the selection.
      this.pendingValue = undefined;
      this.cdr.markForCheck();
    });
  }

  private fuzzyFilter(items: DropDownItem[], text: string): DropDownItem[] {
    const query = text.trim();
    if (!query) return items;
    const searcher = new FuzzySearch(items, ['title'], { caseSensitive: false });
    return searcher.search(query);
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this._searchText.next(value);
    this.searchTextChanged.emit(value);
    this._selectedItem.next(null);
    this.onChange(null);
    this.highlightedIndex = -1;
    this.openPanel();
  }

  public onFocus(): void {
    this.openPanel();
  }

  public onBlur(): void {
    this.onTouched();
  }

  public selectItem(item: DropDownItem): void {
    if (item.getSelectionIsDisabled()) return;
    this._selectedItem.next(item);
    this._searchText.next(item.getSelectionTitle());
    this.onChange(item.getSelectionValue());
    this.onTouched();
    this.itemSelected.emit(item);
    this.closePanel();
  }

  public clear(): void {
    this._searchText.next('');
    this._selectedItem.next(null);
    this.onChange(null);
    this.onTouched();
    this.searchTextChanged.emit('');
    this.cleared.emit();
    this.closePanel();
  }

  @HostListener('document:click', ['$event'])
  public handleClickOutside(event: MouseEvent): void {
    const target = event.target as Node;
    const insideHost = this.eref.nativeElement.contains(target);
    const insideDropdown = this.dropdownContent?.nativeElement.contains(target);
    if (!insideHost && !insideDropdown) {
      this.closePanel();
    }
  }

  @HostListener('keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    combineLatest([this.filteredItems$.pipe(take(1)), this._showDropdown.pipe(take(1))]).subscribe(
      ([items, isOpen]) => {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!isOpen) this.openPanel();
            if (items.length) this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (items.length) this.highlightedIndex = (this.highlightedIndex - 1 + items.length) % items.length;
            break;
          case 'Enter': {
            event.preventDefault();
            const highlighted = items[this.highlightedIndex];
            if (highlighted) this.selectItem(highlighted);
            break;
          }
          case 'Escape':
            event.preventDefault();
            this.closePanel();
            break;
        }
      }
    );
  }

  private openPanel(): void {
    if (this.disabled) return;
    this._showDropdown.next(true);
    if (this.popperInstance) {
      void this.popperInstance.update();
    } else {
      this.positionDropdown();
    }
  }

  private closePanel(): void {
    this._showDropdown.next(false);
    this.highlightedIndex = -1;
    this.popperInstance?.destroy();
    this.popperInstance = undefined;
  }

  private positionDropdown(): void {
    const dropdownElement = this.dropdownContent?.nativeElement;
    const anchorElement = this.anchor?.nativeElement;
    if (!dropdownElement || !anchorElement) return;

    if (dropdownElement.parentElement !== document.body) {
      document.body.appendChild(dropdownElement);
    }

    this.popperInstance = createPopper(anchorElement, dropdownElement, {
      placement: this.dropdownPlacement,
      strategy: 'fixed',
      modifiers: [
        sameWidth,
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'eventListeners', enabled: true, options: { scroll: true, resize: true } }
      ]
    });
  }

  public trackById = TrackByUtils.trackById;
}
